import React from "react";
import styled from "@emotion/styled";
import { AnswerInput, QuestionOptionValue } from "../../../../../api/src/types";
import {
  COLOR_GREEN_BORDER,
  COLOR_GREEN_CHECKBOX_HOVER,
  COLOR_PAGE_BACKGROUND,
  BORDER_RADIUS_MEDIUM,
  SPACING,
  FLEX_COLUMN,
} from "../../../components/theme";
import { ParagraphSmall } from "../../../components/typography/Paragraph";
import Checkbox from "../../../components/icons/Checkbox";
import { IconSize } from "../../../components/theme";
import { parseAndFormatCurrency } from "../../../utils/format";

const AnswerContainer = styled.div`
  ${FLEX_COLUMN}
  gap: ${SPACING.SM};
  border-radius: ${BORDER_RADIUS_MEDIUM};
  box-sizing: border-box;
  width: 100%;
`;

const CheckedAnswerContainer = styled(AnswerContainer)`
  align-items: stretch;
  padding: ${SPACING.LG};
  margin: ${SPACING.SM} 0;
  background-color: ${COLOR_GREEN_CHECKBOX_HOVER};
  border: 1px solid ${COLOR_GREEN_BORDER};
`;

const BoxAnswerContainer = styled(AnswerContainer)<{ isResolved?: boolean }>`
  align-items: flex-start;
  padding: 24px 40px;
  margin: 0;
  background-color: ${({ isResolved }) =>
    isResolved ? COLOR_GREEN_CHECKBOX_HOVER : COLOR_PAGE_BACKGROUND};
  border: none;
`;

const CheckedAnswerTextContainer = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 32px 1fr;
  flex: none;
`;

const BoxAnswerTextContainer = styled.div`
  display: block;
  align-items: center;
  flex: 1;
`;

const AnswerLabel = styled(ParagraphSmall)`
  opacity: 0.6;
`;

interface ReadOnlyAnswerDisplayProps {
  answer: AnswerInput;
  questionOptions: QuestionOptionValue[];
  isChecked?: boolean;
  isResolved?: boolean;
  label?: string;
  inputType?: string | null; // Used to determine if currency formatting is needed
}

export const ReadOnlyAnswerDisplay: React.FC<ReadOnlyAnswerDisplayProps> = ({
  answer,
  questionOptions,
  isChecked = false,
  isResolved = false,
  label,
  inputType,
}) => {
  const Container = isChecked ? CheckedAnswerContainer : BoxAnswerContainer;
  const TextContainer = isChecked
    ? CheckedAnswerTextContainer
    : BoxAnswerTextContainer;

  if (answer.valueTyped) {
    const displayValue =
      inputType === "CURRENCY"
        ? (parseAndFormatCurrency(answer.valueTyped) ?? answer.valueTyped)
        : answer.valueTyped;

    return (
      <Container isResolved={isResolved}>
        {label && <AnswerLabel>{label}</AnswerLabel>}
        <TextContainer>
          <ParagraphSmall textAlign="left">{displayValue}</ParagraphSmall>
        </TextContainer>
      </Container>
    );
  }

  if (answer.answerQuestionOptions.length > 0) {
    const selectedOptionIds = answer.answerQuestionOptions.map(
      (aqo) => aqo.questionOptionId,
    );

    const selectedOptions = questionOptions.filter((option) =>
      selectedOptionIds.includes(option.id),
    );

    return (
      <Container isResolved={isResolved}>
        {label && <AnswerLabel>{label}</AnswerLabel>}
        {selectedOptions.map((option) => (
          <TextContainer key={option.id}>
            {isChecked && <Checkbox isChecked={true} size={IconSize.M} />}
            <ParagraphSmall
              dangerouslySetInnerHTML={{ __html: option.value }}
              textAlign="left"
            />
          </TextContainer>
        ))}
      </Container>
    );
  }

  return (
    <Container isResolved={isResolved}>
      {label && <AnswerLabel>{label}</AnswerLabel>}
      <TextContainer>
        <ParagraphSmall textAlign="left">No answer provided</ParagraphSmall>
      </TextContainer>
    </Container>
  );
};
